import { request } from 'api/request';

export type Portal = {
  shortId: string;
  name: string;
  description?: string;
  // deprecated
  slugPath: string;
  url: string;
  isActive: boolean;
  isPasswordProtected: boolean;
  securityType?: SecurityType;
  numForms: number;
  numLinks: number;
  isDefault: boolean;
};

export type PortalDetail = Portal & {
  forms: PortalForm[];
  links: PortalLink[];
  description: string;
  companySlug: string;
  password: string;
  favicon: string | null;
  logo: string | null;
  banner: string | null;
  hasCustomDomain: boolean;
  slug: string;
};

export type PortalForm = {
  shortId: string;
  order: number;
  name: string;
  description?: string;
  imageUrl?: string;
  journeyShortId: string;
  formShortId: string;
  workspaceShortId: string;
  hasAccessToWorkspace: boolean;
};

export type PortalLink = {
  shortId: string;
  order: number;
  name: string;
  description?: string;
  imageUrl?: string;
  link: string;
};

type PortalItemOrder = {
  shortId: string;
  order: number;
  type: 'form' | 'link';
};

type UpdatePortalItemOrderData = {
  items: PortalItemOrder[];
};

export const loadPortals = async () => {
  const response = await request.get<Portal[]>('/v1/portals/');
  return response.data;
};

export type CreatePortalData = {
  name: string;
};

export const createPortal = async (data: CreatePortalData) => {
  const response = await request.post<{ shortId: string }>('/v1/portals/', data);
  return response.data;
};

export const loadPortal = async (portalShortId: string) => {
  const response = await request.get<PortalDetail>(`/v1/portals/${portalShortId}/`);
  return response.data;
};

export const updatePortal = async (portalShortId: string, data: FormData | Record<string, any>) => {
  let requestData = data;

  if (!(data instanceof FormData) && typeof data === 'object') {
    const formData = new FormData();
    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        formData.append(key, value as string);
      }
    });
    requestData = formData;
  }

  const response = await request.patch<PortalDetail>(`v1/portals/${portalShortId}/`, requestData);
  return response.data;
};

export const deletePortal = async (portalShortId: string) => {
  const response = await request.delete(`/v1/portals/${portalShortId}/`);
  return response.data;
};

export const updatePortalItemOrder = async (
  portalShortId: string,
  data: UpdatePortalItemOrderData,
) => {
  const response = await request.patch(`/v1/portals/${portalShortId}/items/order/`, data);
  return response.data;
};

export const loadPortalLink = async (portalShortId: string, linkShortId: string) => {
  const response = await request.get<PortalLink>(
    `/v1/portals/${portalShortId}/links/${linkShortId}/`,
  );
  return response.data;
};

export const createPortalLink = async (portalShortId: string, data: FormData) => {
  const response = await request.post<PortalLink>(`/v1/portals/${portalShortId}/links/`, data);
  return response.data;
};

export const updatePortalLink = async (
  portalShortId: string,
  linkShortId: string,
  data: FormData,
) => {
  const response = await request.patch<PortalLink>(
    `/v1/portals/${portalShortId}/links/${linkShortId}/`,
    data,
  );
  return response.data;
};

export const deletePortalLink = async (portalShortId: string, linkShortId: string) => {
  const response = await request.delete(`/v1/portals/${portalShortId}/links/${linkShortId}/`);
  return response.data;
};

export type WorkspaceWithJourneyForms = {
  shortId: string;
  title: string;
  forms: JourneyFormWithJourney[];
};

type JourneyFormWithJourney = {
  shortId: string;
  name: string;
  portalDescription: string;
  journeyName: string;
  journeyShortId: string;
};

export const loadWorkspaceJourneyForms = async (portalShortId: string) => {
  const response = await request.get<WorkspaceWithJourneyForms[]>(
    `/v1/portals/${portalShortId}/workspace-forms/`,
  );
  return response.data;
};

export type CreatePortalFormData = {
  form: string;
};

export const addJourneyFormToPortal = async (portalShortId: string, data: CreatePortalFormData) => {
  const response = await request.post<PortalForm>(`/v1/portals/${portalShortId}/forms/`, data);
  return response.data;
};

export const removeJourneyFormFromPortal = async (
  portalShortId: string,
  formShortId: string,
  useJourneyFormId?: boolean,
) => {
  const queryParam = useJourneyFormId ? '?use_journey_form_id=1' : '';
  const response = await request.delete(
    `/v1/portals/${portalShortId}/forms/${formShortId}/${queryParam}`,
  );
  return response.data;
};

type PortalDomain = {
  domain: string;
  has_ssl: boolean;
  status:
    | 'ACTIVE_SSL'
    | 'ACTIVE_SSL_PROXIED'
    | 'ACTIVE_NO_SSL'
    | 'TARGET_NOT_LOADING'
    | 'DNS_INCORRECT'
    | 'DNS_NOT_RESOLVING'
    | 'UNKNOWN';
};

export const loadPortalDomain = async (portalShortId: string) => {
  const response = await request.get<PortalDomain>(`v1/portals/${portalShortId}/portal-domain/`);
  return response.data;
};

export const editPortalDomain = async (portalShortId: string, data: { domain: string }) => {
  const response = await request.patch(`v1/portals/${portalShortId}/portal-domain/`, data);
  return response.data;
};

export type DateFilterOption =
  | 'today'
  | 'last7Days'
  | 'last30Days'
  | 'last90Days'
  | 'pastYear'
  | 'dateToNow'
  | 'chooseADateRange';

export type GroupByOption = 'day' | 'week' | 'month' | 'entireRange';

export type PortalStatisticsFilters = {
  portalShortIds?: string[];
  dateFilter: DateFilterOption;
  groupBy: GroupByOption;
  startDate?: string;
  endDate?: string;
};

export type FormDataPoint = {
  date: string;
  views: number;
  submissions: number;
  clicks: number;
};

export type LinkDataPoint = {
  date: string;
  clicks: number;
};

type PortalStatisticsResponse = {
  totalViews: number;
  viewsDelta: number;
  totalSubmissions: number;
  submissionsDelta: number;
  totalClicks: number;
  clicksDelta: number;
  totalForms: number;
  totalLinks: number;
  formData: FormDataPoint[];
  linkData: LinkDataPoint[];
};

export const loadPortalStatistics = async (filters: PortalStatisticsFilters) => {
  const params = new URLSearchParams();

  if (filters.portalShortIds && filters.portalShortIds.length > 0) {
    params.append('portal_short_ids', filters.portalShortIds.join(','));
  }

  params.append('date_filter', filters.dateFilter);
  params.append('group_by', filters.groupBy);

  if (filters.startDate) {
    params.append('start_date', filters.startDate);
  }

  if (filters.endDate) {
    params.append('end_date', filters.endDate);
  }

  const response = await request.get<PortalStatisticsResponse>(
    `/v1/portals/statistics/?${params.toString()}`,
  );
  return response.data;
};

export type SecurityType = 'none' | 'password' | 'azure_sso';

export type SSOPrefillFieldMode = 'prefill_only' | 'prefill_and_lock' | 'do_not_use';

export type PortalSecuritySettings = {
  securityType: SecurityType;
  password: string;
  azureTenantId: string | null;
  azureClientId: string;
  azureAuthorizedGroupId: string | null;
  ssoPrefillEnabled: boolean;
  ssoPrefillMode: SSOPrefillFieldMode;
};

export type UpdatePortalSecurityData = {
  securityType?: SecurityType;
  newPassword?: string;
  azureTenantId?: string;
  azureClientId?: string;
  azureAuthorizedGroupId?: string;
  invalidateAllSessions?: boolean;
  ssoPrefillMode?: SSOPrefillFieldMode;
};

export const loadPortalSecurity = async (portalShortId: string) => {
  const response = await request.get<PortalSecuritySettings>(
    `/v1/portals/${portalShortId}/security/`,
  );
  return response.data;
};

export const updatePortalSecurity = async (
  portalShortId: string,
  data: UpdatePortalSecurityData,
) => {
  const response = await request.patch<PortalSecuritySettings>(
    `/v1/portals/${portalShortId}/security/`,
    data,
  );
  return response.data;
};
